import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { BRAND } from '@/config/brand';

export default function Navbar() {
    const { t } = useTranslation(['admin']);
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
    };

    return (
        <>
            <nav
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 pt-[env(safe-area-inset-top,0px)] ${
                    scrolled ? 'bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5' : ''
                }`}
            >
                <div className="max-w-6xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        {/* Logo */}
                        <div className="flex items-center gap-2">
                            <img 
                                                                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/4a1265ef2_r.png" 
                                                                alt={BRAND.name} 
                                                                className="h-8 w-auto"
                                                            />
                            <span className="text-lg font-semibold text-white">{BRAND.name}</span>
                        </div>

                        {/* Desktop Nav */}
                        <div className="hidden md:flex items-center gap-8">
                            <button onClick={() => scrollTo('services')} className="text-sm text-white/50 hover:text-white transition-colors">
                                {t('admin:landing.nav.services')}
                            </button>
                            <button onClick={() => scrollTo('cases')} className="text-sm text-white/50 hover:text-white transition-colors">
                                {t('admin:landing.nav.results')}
                            </button>
                            <button onClick={() => scrollTo('testimonials')} className="text-sm text-white/50 hover:text-white transition-colors">
                                {t('admin:landing.nav.testimonials')}
                            </button>
                            <button onClick={() => scrollTo('pricing')} className="text-sm text-white/50 hover:text-white transition-colors">
                                {t('admin:landing.nav.pricing')}
                            </button>
                        </div>

                        {/* CTA */}
                        <div className="hidden md:flex items-center gap-3">
                            <Link to={createPageUrl('Login')}>
                                <Button variant="ghost" className="h-10 px-5 text-white/60 hover:text-white hover:bg-white/10 rounded-full text-sm font-medium">
                                    {t('admin:landing.nav.clientSpace')}
                                </Button>
                            </Link>
                            <Button
                                onClick={() => scrollTo('contact')}
                                className="h-10 px-5 bg-white text-black hover:bg-white/90 rounded-full text-sm font-medium"
                            >
                                {t('admin:landing.nav.contact')}
                            </Button>
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="md:hidden text-white p-2"
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 bg-[#0a0a0a]/95 backdrop-blur-xl pt-24 px-6 md:hidden"
                    >
                        <div className="flex flex-col gap-2">
                            {[
                                { labelKey: 'services', id: 'services' },
                                { labelKey: 'results', id: 'cases' },
                                { labelKey: 'testimonials', id: 'testimonials' },
                                { labelKey: 'pricing', id: 'pricing' },
                                { labelKey: 'contact', id: 'contact' },
                            ].map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => scrollTo(item.id)}
                                    className="py-4 text-2xl font-medium text-white/70 hover:text-white text-left border-b border-white/5 transition-colors"
                                >
                                    {t(`admin:landing.nav.${item.labelKey}`)}
                                </button>
                            ))}
                            <Link
                                to={createPageUrl('Login')}
                                className="py-4 text-2xl font-medium text-white/70 hover:text-white text-left border-b border-white/5 transition-colors"
                            >
                                {t('admin:landing.nav.clientSpace')}
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}